import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import HandshakeIcon from "@mui/icons-material/Handshake";

import {
  DEFAULT_AGREEMENT_CONFIG,
  getAgreementConfig,
  saveAgreementConfig,
} from "../../../services/backend/AgreementConfigService";

// Configuración del módulo de Convenios — las cifras que gobiernan el tablero,
// guardadas en config/agreements.
//
// El catálogo documental de las entidades, los sectores, el bucket y el registro
// público se editaban aquí y se mudaron a Configuración → Entidades: una entidad
// no le pertenece a Convenios, la van a consumir también los contratos.
//
// Cerrada al ROL de ADMIN, no a un permiso: los permisos son una foto por
// usuario, así que un permiso nuevo no existiría en las cuentas ya creadas
// (mismo criterio que FiledConfig y ApplicantConfig).
export default function AgreementConfig() {
  const user = useSelector((state) => state.user.value);
  const isAdmin = !!(user?.roles || []).includes("ADMIN");

  const [cfg, setCfg] = useState(DEFAULT_AGREEMENT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      setCfg(await getAgreementConfig());
      setLoading(false);
    })();
  }, [isAdmin]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    const ok = await saveAgreementConfig({
      soonDays: cfg.soonDays,
      budgetWarningPercentage: cfg.budgetWarningPercentage,
    });
    setSaving(false);
    setFeedback(ok
      ? { severity: "success", msg: "Configuración guardada." }
      : { severity: "error", msg: "No fue posible guardar la configuración." });
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6">Sin acceso</Typography>
        <Typography variant="body2" color="text.secondary">
          Sólo un administrador puede configurar el módulo de Convenios.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, mx: "auto" }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <HandshakeIcon color="primary" />
        <Typography variant="h4">Configuración de convenios</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Las cifras que gobiernan el tablero de convenios. Los documentos que se le exigen a cada
        entidad, los sectores y el registro público se configuran en{" "}
        <strong>Configuración → Entidades</strong>.
      </Typography>

      {feedback && (
        <Alert severity={feedback.severity} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
          {feedback.msg}
        </Alert>
      )}

      {loading ? (
        <Typography>Cargando…</Typography>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Cifras del tablero</Typography>
            <Stack spacing={3} sx={{ maxWidth: 520 }}>
              <TextField type="number" fullWidth label="«Próximo a vencer»: días"
                value={cfg.soonDays ?? 30}
                onChange={(e) => set("soonDays", Math.max(1, Number(e.target.value) || 1))}
                inputProps={{ min: 1, step: 1 }}
                helperText="Con cuántos días de anticipación el tablero marca un convenio como próximo a vencer." />
              <TextField type="number" fullWidth label="Aviso de presupuesto comprometido (%)"
                value={cfg.budgetWarningPercentage ?? 85}
                onChange={(e) => set("budgetWarningPercentage", Math.min(100, Math.max(1, Number(e.target.value) || 1)))}
                inputProps={{ min: 1, max: 100, step: 1 }}
                helperText="A partir de qué porcentaje comprometido se pinta el aviso. El 100% se marca siempre como error." />
            </Stack>
            <Box sx={{ mt: 3, textAlign: "right" }}>
              <Button variant="contained" disabled={saving} onClick={handleSave}>
                {saving ? "Guardando…" : "Guardar cifras"}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
